import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { makeStyles } from '@material-ui/styles';
import {
  TextField,
  Popper,
  Paper,
  MenuItem,
  Typography,
  CircularProgress,
  ClickAwayListener,
  InputAdornment,
} from '@material-ui/core';

import { axiosInstance } from 'common/network';

const useStyles = makeStyles(theme => ({
  popper: {
    zIndex: theme.zIndex.modal,
  },
  paper: {
    maxHeight: 320,
    overflowY: 'auto',
    marginTop: theme.spacing(0.5),
  },
  message: {
    padding: theme.spacing(1, 2),
  },
}));

const DEBOUNCE_MS = 300;

// Controlled string autocomplete over an analytics dimension (type/key/applicationName). Suggestions are
// the read model's distinct values for the field, fetched from /v3/analytics/facets. Built on core
// components (no @material-ui/lab). The value is a plain string owned by the caller, so Clear/Apply reset
// it for free; picking a suggestion or typing free text both flow through onChange.
const AnalyticsFacetAutocomplete = props => {
  const { label, field, value, onChange } = props;
  const classes = useStyles();

  const anchorRef = useRef(null);
  const debounceRef = useRef(null);
  const requestSeq = useRef(0);

  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const runSearch = term => {
    const seq = requestSeq.current + 1;
    requestSeq.current = seq;
    setLoading(true);
    axiosInstance
      .get(process.env.REACT_APP_API_ROOT + 'v3/analytics/facets', { params: { field, q: term, limit: 10 } })
      .then(response => {
        if (seq !== requestSeq.current) return;
        setOptions(Array.isArray(response.data) ? response.data : []);
        setLoading(false);
        setOpen(true);
      })
      .catch(() => {
        if (seq !== requestSeq.current) return;
        setOptions([]);
        setLoading(false);
        setOpen(true);
      });
  };

  const scheduleSearch = term => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(term), DEBOUNCE_MS);
  };

  const handleInput = event => {
    const text = event.target.value;
    onChange(text);
    scheduleSearch(text.trim());
  };

  const handleFocus = () => {
    if (options.length) { setOpen(true); return; }
    // Empty query lists the top distinct values so the field works as a picker without typing.
    runSearch((value || '').trim());
  };

  const pick = option => {
    onChange(option);
    setOpen(false);
  };

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <div ref={anchorRef}>
        <TextField
          fullWidth
          size="small"
          variant="outlined"
          label={label}
          value={value || ''}
          onChange={handleInput}
          onFocus={handleFocus}
          InputProps={{
            endAdornment: loading ? (
              <InputAdornment position="end"><CircularProgress size={18} /></InputAdornment>
            ) : null,
          }}
        />
        <Popper
          className={classes.popper}
          open={open}
          anchorEl={anchorRef.current}
          placement="bottom-start"
          disablePortal
          style={{ width: anchorRef.current ? anchorRef.current.clientWidth : undefined }}
        >
          <Paper className={classes.paper}>
            {options.length === 0 ? (
              <Typography className={classes.message} variant="body2" color="textSecondary">
                {loading ? 'Searching…' : 'No matching values.'}
              </Typography>
            ) : (
              options.map(option => (
                <MenuItem key={option} onClick={() => pick(option)}>
                  {option}
                </MenuItem>
              ))
            )}
          </Paper>
        </Popper>
      </div>
    </ClickAwayListener>
  );
};

AnalyticsFacetAutocomplete.propTypes = {
  label: PropTypes.string,
  field: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};

export default AnalyticsFacetAutocomplete;
